import React, { useState } from "react";
import { ErrorMessage, useFormikContext } from "formik";
import { Select, ConfigProvider } from "antd";
import { DownOutlined } from "@ant-design/icons";
import useThemeStore from "(store)/themeStore";
import "../../styles/SelectorField.css";
import { getValueFromPath } from "utils";
import { Tooltip } from "components";
import { ExclamationCircleIcon } from "assets/icons";

const SearchSelectField = ({
  label,
  name,
  placeholder,
  options = [],
  isRequired = false,
  disabled = false,
  isDynamic = false,
  tooltipValue,
  onChange,
}) => {
  const { errors, touched, setFieldValue, values, setFieldTouched } =
    useFormikContext();
  const theme = useThemeStore((state) => state.theme);
  const showError =
    (isDynamic ? getValueFromPath(errors, name) : errors[name]) &&
    (isDynamic ? getValueFromPath(touched, name) : touched[name]);
  const [isFocused, setIsFocused] = useState(false);

  // Determine input and dropdown class based on theme, error, and focus state
  const selectClass =
    theme === "light" ? "custom-select-input" : "custom-select-input-dark";

  // Custom styles for select input
  const customStyle = {
    border: showError
      ? "1px solid #ef4444" // Red border for error
      : isFocused
      ? "1px solid #1996A6" // Blue border when focused
      : "", // Default border
    borderRadius: "10px",
  };

  // Custom arrow color based on theme
  const customArrow = <DownOutlined style={{ color: "gray" }} />;

  return (
    <div className="w-full">
      {label && (
        <div className="flex items-center gap-1 mb-1">
          <span className="flex gap-1 items-end relative">
            <span className="label-text dark:text-white">{label}</span>
            {isRequired && (
              <span className="text-red-500 font-extrabold">*</span>
            )}
          </span>
          {tooltipValue && (
            <div className="pl-2">
              <Tooltip
                children={<ExclamationCircleIcon color={"red"} />}
                message={tooltipValue}
              />
            </div>
          )}
        </div>
      )}

      <ConfigProvider
        theme={{
          components: {
            Select: {
              zIndexPopupBase: 99999,
              optionSelectedBg: "#ddeff0",
              borderRadius: 8,
              colorTextPlaceholder: "gray",
              colorText: theme === "light" ? "black" : "white",
              optionSelectedColor: "#020617",
              colorBorder: showError ? "red" : "gray",
              selectorBg: theme === "light" ? "white" : "black",
              optionPadding: "7px 20px",
              colorBgContainerDisabled: "#f3f4f6",
              colorTextDisabled: "rgb(148 163 184)",
            },
          },
        }}
      >
        <Select
          variant={isFocused || showError ? "borderless" : "outlined"}
          onChange={(value) => {
            setFieldValue(name, value);

            if (onChange) {
              onChange(value);
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            setFieldTouched(name, true);
          }}
          value={getValueFromPath(values, name) || values[name] || null}
          showSearch
          style={{ width: "100%", ...customStyle, height: "38px" }} // Apply custom styles here
          className={`dark:bg-black dark:text-white custom-select ${
            showError ? "border-red-500" : selectClass
          }`}
          placeholder={placeholder}
          optionFilterProp="label"
          // filterSort={(optionA, optionB) =>
          //   (optionA?.label ?? "")
          //     .toLowerCase()
          //     .localeCompare((optionB?.label ?? "").toLowerCase())
          // }
          popupClassName="custom-select-dropdown"
          options={options}
          disabled={disabled}
          suffixIcon={customArrow} // Apply custom arrow icon
        />
      </ConfigProvider>

      {showError && (
        <ErrorMessage
          className="text-xs text-red-500 mt-1"
          name={name}
          component="div"
        />
      )}
    </div>
  );
};

export default SearchSelectField;
